import React, { useRef, useEffect } from "react";
import classNames from "classnames";

type VideoBackgroundProps = {
  className?: string;
};

const VideoBackground = ({ className }: VideoBackgroundProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const isBrowser = typeof window !== "undefined";
    const motionQuery =
      isBrowser && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;

    const playVideo = async () => {
      try {
        video.muted = true;
        video.defaultMuted = true;

        await video.play();
      } catch (error) {
        console.warn("Autoplay prevented, video will play on user interaction: ", error);
      }
    };

    const handleCanPlay = () => {
      if (!motionQuery?.matches) {
        playVideo();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && video.paused && !motionQuery?.matches) {
        playVideo();
      }
    };

    const handleMotionPreferenceChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        video.preload = "none";
        video.pause();
      } else {
        video.preload = "auto";
        playVideo();
      }
    };

    video.addEventListener("canplay", handleCanPlay);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    motionQuery?.addEventListener("change", handleMotionPreferenceChange);

    if (motionQuery?.matches) {
      video.preload = "none";
      video.pause();
    } else if (video.readyState >= 3) {
      video.preload = "auto";
      playVideo();
    }

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      motionQuery?.removeEventListener("change", handleMotionPreferenceChange);
    };
  }, []);

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.warn("Background video failed to load: ", e);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <video
      ref={videoRef}
      autoPlay
      loop
      muted
      playsInline
      disablePictureInPicture
      preload="auto"
      className={classNames("video-background", className)}
      onError={handleVideoError}
      onClick={handleClick}
      style={{ pointerEvents: "none" }}
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src="/SharkBackgroundVideo.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
};

export default VideoBackground;
